/* global packListUrl packListUpdateUrl packCategoriesApiUrl csrfToken */

import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { useToast } from 'Hooks/useToast';

const PacksContext = createContext(undefined);
const PacksDispatchContext = createContext(undefined);

function ApiProvider({ processedAction, setProcessedAction, children }) {
    const [latestResponse, setLatestResponse] = useState({});
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [packs, setPacks] = useState([]);
    const [simplePacks, setSimplePacks] = useState([]);
    const [contractorPacks, setContractorPacks] = useState([]);
    const [packItems, setPackItems] = useState([]);
    const [simplePackItems, setSimplePackItems] = useState([]);
    const [contractorPackItems, setContractorPackItems] = useState([]);
    const [simplePackCategories, setSimplePackCategories] = useState([]);
    const [contractorPackCategories, setContractorPackCategories] = useState([]);
    const [venues, setVenues] = useState([]);
    const [relations, setRelations] = useState({});
    const [popup, setPopup] = useState(false);
    const [forceUpdateTop, setForceUpdateTop] = useState(0);
    const [isSaving, setIsSaving] = useState(false);

    const stateRef = useRef({
        packs: [],
        packItems: [],
        venues: [],
        simplePackCategories: [],
        contractorPackCategories: [],
        relations: {},
        processedAction: null,
        setProcessedAction: () => {},
    });

    const toast = useToast();

    const refetch = useCallback(() => {
        return fetch(packListUrl)
            .then((res) => res.json())
            .then((result) => {
                setLatestResponse(result);
                setIsLoaded(true);
                return result;
            });
    }, []);

    const apiCall = useCallback((action, details = false, totalDuration = 0) => {
        const act = JSON.parse(JSON.stringify(processedAction));
        // this function calls the function name from "action", and sends the details
        switch (action) {
            case 'form':
                setPopup(details); // if details is false popup closes
                break;
            case 'packEdit':
                if (details.id) {
                    act.action = 'update';
                    act.itemType = 'pack';
                    act.itemId = details.id;
                    setProcessedAction(act);

                    updatePack(details);
                    return;
                }

                act.action = 'add';
                act.itemType = 'pack';
                setProcessedAction(act);

                createPack(details);
                break;
            case 'packItemEdit':
                if (details.id) {
                    act.action = 'update';
                    act.itemType = 'pack_item';
                    act.itemId = details.id;
                    setProcessedAction(act);

                    updatePackItem(details);
                    return;
                }

                act.action = 'add';
                act.itemType = 'pack_item';
                setProcessedAction(act);

                createPackItem(details);
                break;
            case 'packDelete':
                if (details.id.toString().startsWith('temp')) {
                    deletePack(details);
                    return;
                }
                details.deleted = true;
                act.action = 'delete';
                act.itemType = 'pack';
                act.itemId = details.id;
                setProcessedAction(act);

                updatePack(details);
                break;
            case 'packItemDelete':
                deletePackItemFromPacks(details);
                if (details.id.toString().startsWith('temp')) {
                    deletePackItem(details);
                    return;
                }
                details.deleted = true;

                act.action = 'delete';
                act.itemType = 'pack_item';
                act.itemId = details.id;
                setProcessedAction(act);

                updatePackItem(details);
                break;
            case 'clonePackItem':
                clonePackItem(details);
                break;
            case 'clearClonePackItem':
                clearClonePackItem();
                break;
            case 'itemToPack':
                itemToPack(details);
                break;
            case 'packToVenueDelete':
                act.action = 'unassign';
                act.itemType = 'pack';
                act.itemId = details.packId;
                act.toType = 'venue';
                act.toId = details.venueId;
                setProcessedAction(act);

                packToVenueDelete(details);
                break;
            case 'packToVenueEdit':
                act.action = 'assign';
                act.itemType = 'pack';
                act.itemId = details.packId;
                act.toType = 'venue';
                act.toId = details.venueId;
                setProcessedAction(act);

                packToVenueEdit(details, totalDuration);
                break;
            case 'packCategoryEdit':
                if (details.id && !details.id.toString().startsWith('temp')) {
                    act.action = 'update';
                    act.itemType = 'pack_category';
                    act.itemId = details.id;
                    setProcessedAction(act);
                    updatePackCategory(details);
                    return;
                }
                act.action = 'add';
                act.itemType = 'pack_category';
                setProcessedAction(act);
                createPackCategory(details);
                break;
            case 'packCategorySave':
                savePackCategory(details);
                break;
            case 'packItemsImportSuccess':
                setLatestResponse(details);
                break;
            case 'save':
                save(details);
                break;
            case 'saveNow':
                save(stateRef.current);
                break;
            case 'refetch':
                refetch();
                break;
            default:
                break;
        }
    }, []);

    useEffect(() => {
        fetch(packListUrl)
            .then((res) => res.json())
            .then(
                (result) => {
                    setLatestResponse((oldResult) => {
                        setIsLoaded(true);
                        return result;
                    });
                },
                (error) => {
                    setError(() => {
                        setIsLoaded(true);
                        return error;
                    });
                },
            )
            .catch((error) => {
                console.log(error);
                alert('ERROR');
            });
    }, []);

    const timeToFloat = (time) => {
        const hoursMinutes = time.toString().split(':');
        const hours = parseInt(hoursMinutes[0], 10);
        const minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;
        return hours + minutes / 60;
    };

    const save = (details, confirmOverflow = false) => {
        setIsLoaded(false);
        setIsSaving(true);

        const det = {
            packItems: [],
            packs: [],
            packCategories: [],
            venues: [],
            confirm_overflow: confirmOverflow,
        };

        const relationList = JSON.parse(JSON.stringify(details.relations));

        const procAction = Object.prototype.hasOwnProperty.call(details, 'processedAction') ? details.processedAction : null;
        if (procAction && procAction.itemType && procAction.action) {
            if (procAction.itemType === 'pack_item') {
                if (procAction.action === 'add') {
                    details.packItems.forEach((item) => {
                        if (typeof item.id === 'string' && item.id.startsWith('temp')) {
                            det.packItems.push(item);
                        }
                    });
                }

                if (procAction.action === 'delete' || procAction.action === 'update') {
                    const id = procAction.itemId;

                    details.packItems.forEach((item) => {
                        if (item.id === id) {
                            det.packItems.push(item);
                        }
                    });

                    const packIds = relationList.pack_children__packs[id];
                    if (packIds.length > 0) {
                        details.packs.forEach((item) => {
                            if (packIds.includes(item.id)) {
                                det.packs.push(item);
                            }
                        });
                    }

                    const venueIds = relationList.pack_children__venues[id];
                    if (venueIds.length > 0) {
                        details.venues.forEach((item) => {
                            if (venueIds.includes(item.id)) {
                                det.venues.push(item);
                            }
                        });
                    }
                }
            } else if (procAction.itemType === 'pack') {
                if (procAction.action === 'add') {
                    details.packs.forEach((item) => {
                        if (typeof item.id === 'string' && item.id.startsWith('temp')) {
                            det.packs.push(item);
                        }
                    });
                }

                if (procAction.action === 'delete' || procAction.action === 'update') {
                    const id = procAction.itemId;

                    details.packs.forEach((item) => {
                        if (item.id === id) {
                            det.packs.push(item);
                        }
                    });

                    const venueIds = relationList.pack__venues[id];
                    if (venueIds.length > 0) {
                        details.venues.forEach((item) => {
                            if (venueIds.includes(item.id)) {
                                det.venues.push(item);
                            }
                        });
                    }
                }

                if ((procAction.action === 'assign' || procAction.action === 'unassign') && procAction.toType === 'venue') {
                    const packId = procAction.itemId;
                    const venueId = procAction.toId;

                    details.packs.forEach((item) => {
                        if (item.id === packId) {
                            det.packs.push(item);
                        }
                    });

                    details.venues.forEach((item) => {
                        if (item.id === venueId) {
                            det.venues.push(item);
                        }
                    });
                }
            } else if (procAction.itemType === 'pack_category') {
                if (procAction.action === 'add') {
                    const allCategories = [...(details.simplePackCategories || []), ...(details.contractorPackCategories || [])];
                    allCategories.forEach((item) => {
                        if (typeof item.id === 'string' && item.id.startsWith('temp')) {
                            det.packCategories.push(item);
                        }
                    });
                }
                if (procAction.action === 'update') {
                    const id = procAction.itemId;
                    const allCategories = [...(details.simplePackCategories || []), ...(details.contractorPackCategories || [])];
                    allCategories.forEach((item) => {
                        if (item.id === id) {
                            det.packCategories.push(item);
                        }
                    });
                }
                det.packItems = details.packItems;
                det.packs = details.packs;
                det.venues = details.venues;
            } else {
                det.packItems = details.packItems;
                det.packs = details.packs;
                det.packCategories = [...(details.simplePackCategories || []), ...(details.contractorPackCategories || [])];
                det.venues = details.venues;
            }
        } else {
            det.packItems = details.packItems;
            det.packs = details.packs;
            det.venues = details.venues;
        }

        fetch(packListUpdateUrl, {
            method: 'POST', // or 'PUT'
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify(det),
        })
            .then((res) => {
                if (!res.ok) {
                    // A refusal states its reason, which is shown as any other error is.
                    return res
                        .json()
                        .catch(() => ({}))
                        .then((body) => {
                            if (body?.error) {
                                return { error: body.error };
                            }
                            throw new Error('save_request_failed');
                        });
                }
                return res.json();
            })
            .then(
                (result) => {
                    // Handle project overflow warning
                    if (result.overflow_warning && result.overflow_data) {
                        // Keep loader displayed while showing confirmation dialog
                        if (confirm(result.overflow_data.message)) {
                            // User confirmed, retry with confirmation flag (loader still showing)
                            save(details, true);
                        } else {
                            // User cancelled - stop loader, close modal, and remount component by reloading data
                            setIsLoaded(true);
                            setIsSaving(false);

                            // Close the modal
                            apiCall('form', false);

                            // Reload the pack list to reset the component state
                            fetch(packListUrl)
                                .then((res) => res.json())
                                .then(
                                    (result) => {
                                        setLatestResponse(result);
                                        setIsLoaded(true);
                                    },
                                    (error) => {
                                        setError(error);
                                        setIsLoaded(true);
                                    },
                                )
                                .catch((error) => {
                                    console.log(error);
                                });
                        }
                        return;
                    }

                    if (result.data?.message) {
                        alert(result.data.message);
                        if (result.data.url) {
                            window.location.href = result.data.url;
                        }
                    }
                    if (result.data?.error || result.error) {
                        alert(result.data?.error || result.error);

                        // The refused edits are still on screen; reload what was saved.
                        fetch(packListUrl)
                            .then((res) => res.json())
                            .then((saved) => setLatestResponse(saved))
                            .catch((error) => console.log(error));
                    } else {
                        toast.success(
                            $t('The updates were successful. Cashflows are being updated in the background and may take several minutes to complete'),
                        );

                        setLatestResponse(result);
                    }
                    setIsLoaded(true);
                    setIsSaving(false);
                    // Close modal after successful save
                    apiCall('form', false);
                },
                (error) => {
                    toast.error($t('Saving is taking longer than expected, please try again'));
                    setError(error);
                    setIsLoaded(true);
                    setIsSaving(false);
                    // Close modal on error
                    apiCall('form', false);
                },
            )
            .catch((error) => {
                toast.error($t('Saving is taking longer than expected, please try again'));
                setIsLoaded(true);
                setIsSaving(false);
                // Close modal on error
                apiCall('form', false);
            })
            .finally(() => {
                setIsSaving(false);
                const pA = JSON.parse(JSON.stringify(procAction));
                Object.keys(pA).forEach((key) => {
                    pA[key] = null;
                });
                details.setProcessedAction(pA);
            });
    };

    const packToVenueDelete = (details) => {
        setVenues((oldVenues) => {
            const newVenues = [...oldVenues];
            const venueIndex = newVenues.findIndex((v) => v.id === details.venueId);
            const packIndex = newVenues[venueIndex].expense_packs.findIndex((p) => p.id === details.packId);
            const relation = newVenues[venueIndex].expense_packs[packIndex];
            if (1 === Object.keys(relation.pivot.amount).length) {
                newVenues[venueIndex].expense_packs.splice(packIndex, 1);
            } else {
                details.filterDays.forEach((day) => {
                    delete relation.pivot.amount[day];
                    delete relation.pivot.details[day];
                    if (relation.pivot.customizations) {
                        delete relation.pivot.customizations[day];
                    }
                });
            }
            setForceUpdateTop((oldValue) => {
                const newValue = oldValue + 1;
                return newValue;
            });
            return newVenues;
        });
    };

    const packToVenueEdit = (details, totalDuration = 0) => {
        setVenues((oldVenues) => {
            const newVenues = [...oldVenues];
            const venueIndex = newVenues.findIndex((v) => v.id === details.venueId);
            const packIndex = newVenues[venueIndex].expense_packs.findIndex((p) => p.id === details.packId);
            const customizationByDate = details.customizations || {};
            if (-1 === packIndex) {
                const amountObj = { expense_id: details.packId };

                const pivot = {
                    amount: {},
                    details: {},
                    customizations: {},
                };
                details.filterDays.forEach((day) => {
                    amountObj[day] = details.amount;

                    const schedule = details.selectedSchedules
                        ? details.selectedSchedules.find((item) => {
                              return item.date === day;
                          })
                        : null;

                    pivot.amount[day] = details.amount;
                    pivot.details[day] =
                        details.checked && schedule
                            ? {
                                  duration: timeToFloat(schedule.duration),
                                  end: schedule.end,
                                  start: schedule.start,
                                  useSessionTime: details?.details?.useSessionTime || false,
                              }
                            : totalDuration.manual;
                    pivot.customizations[day] = customizationByDate[day] || {};
                });
                newVenues[venueIndex].expense_packs.push({
                    id: details.packId,
                    pivot,
                });
            } else {
                const amountObj = _.isArray(newVenues[venueIndex].expense_packs[packIndex].pivot.amount)
                    ? {}
                    : newVenues[venueIndex].expense_packs[packIndex].pivot.amount;
                const detailsObj = newVenues[venueIndex].expense_packs[packIndex].pivot.details;
                const customizationsObj = newVenues[venueIndex].expense_packs[packIndex].pivot.customizations || {};
                details.filterDays.forEach((day) => {
                    const schedule = details.selectedSchedules
                        ? details.selectedSchedules.find((item) => {
                              return item.date === day;
                          })
                        : null;

                    amountObj[day] = details.amount;
                    detailsObj[day] =
                        details.checked && schedule
                            ? {
                                  duration: timeToFloat(schedule.duration),
                                  end: schedule.end,
                                  start: schedule.start,
                                  useSessionTime: details?.details?.useSessionTime || false,
                              }
                            : totalDuration.manual;
                    customizationsObj[day] = customizationByDate[day] || {};
                });

                newVenues[venueIndex].expense_packs[packIndex].pivot.amount = amountObj;
                newVenues[venueIndex].expense_packs[packIndex].pivot.details = detailsObj;
                newVenues[venueIndex].expense_packs[packIndex].pivot.customizations = customizationsObj;
            }
            setForceUpdateTop((oldValue) => {
                if (typeof oldValue !== 'undefined') {
                    const newValue = oldValue + 1;

                    return newValue;
                } else {
                    return 0;
                }
            });
            return newVenues;
        });
    };

    const deletePackItemFromPacks = useCallback((details) => {
        setPacks((packs) => {
            const tempItem = details.id.toString().startsWith('temp') ? true : false;
            const packsWithItem = [];
            for (let i = 0; i < packs.length; i++) {
                const pack = packs[i];
                if (-1 !== pack.pack_children.findIndex((item) => item.id == details.id)) {
                    packsWithItem.push(i);
                }
            }
            if (!packsWithItem.length) {
                // no pack contain the item, return the initial packs
                return packs;
            }
            const newPacks = [...packs];
            for (let i = 0; i < packsWithItem.length; i++) {
                const packIndex = packsWithItem[i];

                newPacks[packIndex].pack_children = newPacks[packIndex].pack_children.filter((item) => item.id != details.id);
            }
            return newPacks;
        });
    }, []);

    const itemToPack = useCallback((details) => {
        apiCall('form', { ...details, type: 'itemToPack' });
    }, []);

    const clonePackItem = useCallback((details) => {
        setPackItems((packItems) => {
            const newPacks = [...packItems];
            const index = newPacks.findIndex((packItems) => packItems.id == details.id);
            newPacks.splice(index, 0, { ...newPacks[index], id: 'temp', temp: true });
            return newPacks;
        });
    }, []);

    const clearClonePackItem = useCallback(() => {
        setPackItems((packItems) => {
            const newPacks = packItems.filter((packItem) => !packItem.temp);
            return newPacks;
        });
    }, []);

    const deletePack = useCallback((details) => {
        setPacks((packs) => {
            const newPacks = [...packs];
            const index = newPacks.findIndex((pack) => pack.id == details.id);
            newPacks.splice(index, 1);
            return newPacks;
        });
        apiCall('form', false);
    }, []);

    const deletePackItem = useCallback((details) => {
        setPackItems((packItems) => {
            const newPackItems = [...packItems];
            const index = newPackItems.findIndex((packItem) => packItem.id == details.id);
            newPackItems.splice(index, 1);
            return newPackItems;
        });
        apiCall('form', false);
    }, []);

    const updatePack = useCallback((details) => {
        setPacks((packs) => {
            const newPacks = [...packs];
            const index = newPacks.findIndex((pack) => pack.id == details.id);
            // clean children with amount 0
            const cleanDetails = {
                ...details,
                pack_children: details.pack_children.filter((item) => item.pivot.amount > 0),
            };
            newPacks[index] = { ...cleanDetails };
            return newPacks;
        });
    }, []);

    const updatePackItem = useCallback((details) => {
        setPackItems((packItems) => {
            const newPackItems = [...packItems];
            const index = newPackItems.findIndex((packItem) => packItem.id == details.id);
            newPackItems[index] = { ...details };
            return newPackItems;
        });
    }, []);

    const createPack = useCallback((details) => {
        setPacks((packs) => {
            const newPacks = [...packs];
            const tempId = 'temp' + uuidv4();
            newPacks.push({ ...details, id: tempId, pack_children: [] });
            return newPacks;
        });
    }, []);

    const createPackItem = useCallback((details) => {
        setPackItems((packsItems) => {
            const newPackItems = [...packsItems];
            const tempId = 'temp' + uuidv4();
            newPackItems.push({ ...details, id: tempId });
            return newPackItems;
        });
    }, []);

    const createPackCategory = useCallback((details) => {
        const tempId = 'temp' + uuidv4();
        const category = { ...details, id: tempId, pack_contractor: details.pack_contractor ?? 0 };
        if (category.pack_contractor) {
            setContractorPackCategories((prev) => [...prev, category]);
        } else {
            setSimplePackCategories((prev) => [...prev, category]);
        }
    }, []);

    const updatePackCategory = useCallback((details) => {
        const updater = (prev) => {
            const idx = prev.findIndex((c) => c.id === details.id);
            if (idx === -1) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx], ...details };
            return next;
        };
        if (details.pack_contractor) {
            setContractorPackCategories(updater);
        } else {
            setSimplePackCategories(updater);
        }
    }, []);

    const savePackCategory = useCallback(
        (details) => {
            const isCreate = !details.id || String(details.id).startsWith('temp');
            const url = isCreate ? packCategoriesApiUrl : `${packCategoriesApiUrl}/${details.id}`;
            const body = isCreate ? { name: details.name, pack_contractor: details.pack_contractor ?? 0 } : { name: details.name };
            const method = isCreate ? 'POST' : 'PUT';

            fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: JSON.stringify(body),
            })
                .then((res) => {
                    if (!res.ok) {
                        return res.json().then((err) => Promise.reject(err));
                    }
                    return res.json();
                })
                .then(
                    (result) => {
                        const category = result.data;
                        if (!category) {
                            toast.error($t('An error occurred while saving the category'));
                            return;
                        }
                        if (isCreate) {
                            if (category.pack_contractor) {
                                setContractorPackCategories((prev) => [...prev, category]);
                            } else {
                                setSimplePackCategories((prev) => [...prev, category]);
                            }
                        } else {
                            updatePackCategory(category);
                        }
                        setPopup(false);
                        toast.success($t('Category saved successfully'));
                    },
                    (err) => {
                        const message = err?.message || err?.errors?.name?.[0] || $t('Failed to save category');
                        toast.error(message);
                    },
                )
                .catch((err) => {
                    toast.error(err?.message || $t('Failed to save category'));
                });
        },
        [toast, updatePackCategory],
    );

    useEffect(() => {
        if (Object.prototype.hasOwnProperty.call(latestResponse, 'data')) {
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'packItems')) {
                setPackItems(latestResponse.data.packItems);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'simplePackItems')) {
                setSimplePackItems(latestResponse.data.simplePackItems);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'contractorPackItems')) {
                setContractorPackItems(latestResponse.data.contractorPackItems);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'simplePackCategories')) {
                setSimplePackCategories(latestResponse.data.simplePackCategories);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'contractorPackCategories')) {
                setContractorPackCategories(latestResponse.data.contractorPackCategories);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'packs')) {
                setPacks(latestResponse.data.packs);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'simplePacks')) {
                setSimplePacks(latestResponse.data.simplePacks);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'contractorPacks')) {
                setContractorPacks(latestResponse.data.contractorPacks);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'relations')) {
                setRelations(latestResponse.data.relations);
            }
            if (Object.prototype.hasOwnProperty.call(latestResponse.data, 'venues')) {
                if (latestResponse.data.venues.length) {
                    for (let i = 0; i < latestResponse.data.venues.length; i++) {
                        const venue = latestResponse.data.venues[i];
                        if (venue && venue.expense_packs && venue.expense_packs.length) {
                            for (let j = 0; j < venue.expense_packs.length; j++) {
                                const expensePack = venue.expense_packs[j];
                                expensePack.pivot.amount = JSON.parse(expensePack.pivot.amount);
                                expensePack.pivot.details = JSON.parse(expensePack.pivot.details);
                                expensePack.pivot.customizations = expensePack.pivot.customizations ? JSON.parse(expensePack.pivot.customizations) : {};
                            }
                        }
                    }
                }
                setVenues(latestResponse.data.venues);
            }
            setIsLoaded(true);
        }
    }, [latestResponse]);

    useEffect(() => {
        stateRef.current = {
            packs,
            packItems,
            venues,
            simplePackCategories,
            contractorPackCategories,
            relations,
            processedAction,
            setProcessedAction,
        };
    }, [packs, packItems, venues, simplePackCategories, contractorPackCategories, relations, processedAction, setProcessedAction]);

    return (
        <PacksContext.Provider
            value={{
                packs,
                simplePacks,
                contractorPacks,
                packItems,
                simplePackItems,
                contractorPackItems,
                simplePackCategories,
                contractorPackCategories,
                popup,
                venues,
                isLoaded,
                forceUpdateTop,
                isSaving,
                relations,
                processedAction,
                setProcessedAction,
            }}
        >
            <PacksDispatchContext.Provider value={apiCall}>{children}</PacksDispatchContext.Provider>
        </PacksContext.Provider>
    );
}

export { ApiProvider, PacksContext, PacksDispatchContext };
